import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { useDropzone } from "react-dropzone"
import * as React from "react"
import { classNames } from "../../../../utils/classNames"
import { Button } from "../../Button/Button"
import { Progress, Spin } from "antd"
import { FormError } from "../../_formComponents/FormError"
import { head } from "ramda"
import { AntdIconWrap } from "../../Icons/AntdIconWrap"
import { PlusLg } from "react-bootstrap-icons"
import { logger } from "../../../../utils/loggerWrap"
import { ImageOptions, ImageUploadData, ImageUploadType } from "../types"
import { Input } from "../../Input/Input"
import { SortableList } from "../../DragAndDrop/SortableList"
import NativeFileDropTarget from "../../../common/dragAndDrop/NativeFileDropTarget"

type Props = {
    className?: string
    maxImages: number
    type: ImageUploadType
    error?: Maybe<string>
    progress?: number
    description?: string
    imageOptions?: ImageOptions
    allowEditUploadedImages: boolean
    onDrop: (files: File[]) => void
    onRemove: (imageId: string) => void
    onEditLabel: (imageId: string, label: string) => void
    images: ImageUploadData[]
    loadingImages: Maybe<string[]>
    openImagePreview: (imgUrl: Maybe<ImageUploadData>) => void
    openImageEdit: (imgUrl: Maybe<ImageUploadData>) => void
    onChangeImages: (images: ImageUploadData[]) => void
}

/**
 * Base UI component without uploading logic and state,
 * for usual cases (image upload) you want to use actual <code>ImageUpload.tsx</code> component.
 */
export const ImageUploadBase = (props: Props) => {
    const remainingImages = props.maxImages > 1 ? props.maxImages - props.images.length : 1
    const isDisabled = remainingImages < 1

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        // Enable selecting multiple files/images based on 'maxImages' prop
        multiple: remainingImages > 1,
        // When selecting multiple files, slice the count according to 'maxImages' prop
        onDrop: (files: File[]) => props.onDrop(files.slice(0, remainingImages)),
        onError: err => {
            logger.error("Error in ImageUploadBase component", null, err)
        },
        accept: {
            "image/*": [".gif", ".jpeg", ".jpg", ".png", ".bmp", ".heic", ".heif"],
        },
        disabled: isDisabled,
    })

    const renderDescription = () => {
        if (props.description) {
            return <div className={classes.desc}>{props.description}</div>
        }
        return null
    }

    const renderUploadArea = () => {
        return (
            <NativeFileDropTarget disabled={isDisabled}>
                <div
                    {...getRootProps()}
                    className={classNames(
                        classes.wrap,
                        isDragActive ? classes.isDragging : classes.isEmpty,
                        props.className,
                        isDisabled && "pointer-events-none opacity-50"
                    )}>
                    <input {...getInputProps()} />
                    <div className={classes.row}>
                        <Button size="small" className={classes.btn}>
                            Choose file
                        </Button>
                        <div className={classes.label}>or drop to upload</div>
                    </div>
                    {renderDescription()}
                    {props.error ? <FormError className={classes.error}>{props.error}</FormError> : null}
                </div>
            </NativeFileDropTarget>
        )
    }

    const renderUploadAreaLoading = () => {
        return (
            <NativeFileDropTarget disabled={true}>
                <div className={classNames(classes.wrap, classes.isLoading, props.className)}>
                    <div className={classes.label}>Uploading {props.progress ? props.progress + "%" : "..."}</div>
                    <Progress percent={props.progress ?? 0} size="small" showInfo={false} />
                </div>
            </NativeFileDropTarget>
        )
    }

    const renderFileName = (image: ImageUploadData) => {
        const url = image.originalImage.url
        return url.split("/").pop()
    }

    // Render vertical multi-images layout with labels
    if (
        props.maxImages > 1 &&
        !!props.images.length &&
        (props.type === ImageUploadType.Multi || props.type === ImageUploadType.MultiWithLabel)
    ) {
        return (
            <div>
                <div className="mb-3">
                    {!!props.loadingImages?.length ? renderUploadAreaLoading() : renderUploadArea()}
                </div>
                <SortableList
                    items={props.images}
                    onChange={props.onChangeImages}
                    keyExtractor={image => image.originalImage.id}
                    className={classes.multiImagesWithLabel.wrap}
                    renderItem={image => (
                        <div
                            className={classes.multiImagesWithLabel.row}
                            style={{
                                cursor: "grab",
                            }}>
                            <div className={classes.multiImagesWithLabel.dragWrap}>
                                <img
                                    src="/dragHandler.svg"
                                    alt="Drag handler"
                                    className={classes.multiImagesWithLabel.dragIcon}
                                />
                            </div>
                            <button
                                type="button"
                                className={classes.multiImagesWithLabel.imgButton}
                                onClick={() => props.openImagePreview(image)}>
                                <img
                                    src={image.originalImage.url}
                                    alt="Image thumbnail"
                                    className={classes.multiImagesWithLabel.imgPreview}
                                />
                            </button>
                            {props.type === ImageUploadType.MultiWithLabel ? (
                                <Input
                                    placeholder="Enter image label"
                                    size="small"
                                    className="mr-4"
                                    value={image.label}
                                    onChange={e => props.onEditLabel(image.originalImage.id, e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key === " ") {
                                            e.stopPropagation()
                                        }
                                    }}
                                />
                            ) : (
                                <div className="truncate pr-2">{renderFileName(image)}</div>
                            )}
                            {/* Remove image button */}
                            <Button
                                className={classes.multiImagesWithLabel.removeBtn}
                                onClick={() => props.onRemove(image.originalImage.id)}>
                                <AntdIconWrap type="delete" className={classes.deleteIcon} />
                            </Button>
                        </div>
                    )}
                />
            </div>
        )
    }

    // Render horizontal multi-images layout
    if (props.maxImages > 1 && !!props.images.length) {
        const canAddMoreImages = (props.loadingImages ?? []).length + props.images.length < props.maxImages
        return (
            <div>
                <div className={classes.multiImages.wrap}>
                    {props.images.map(image => (
                        // Render clickable, uploaded images
                        <div key={image.originalImage.id} className={classNames(classes.multiImages.base)}>
                            <button
                                type="button"
                                className={classes.multiImages.imgButton}
                                onClick={() => props.openImagePreview(image)}>
                                <img
                                    src={image.originalImage.url}
                                    alt="Image thumbnail"
                                    className={classes.multiImages.img}
                                />
                            </button>
                            {/* Remove image button */}
                            <Button className={classes.closeBtn} onClick={() => props.onRemove(image.originalImage.id)}>
                                <AntdIconWrap type="delete" className={classes.deleteIcon} />
                            </Button>
                        </div>
                    ))}
                    {(props.loadingImages ?? []).map(loadingImg => (
                        // Render in-progress loadings
                        <div
                            key={loadingImg}
                            className={classNames(classes.multiImages.base, classes.multiImages.loading)}>
                            <Spin size="small" />
                            <div className="text-13 text-black/60">Uploading...</div>
                        </div>
                    ))}
                    <NativeFileDropTarget disabled={!canAddMoreImages}>
                        {canAddMoreImages ? (
                            // Render "add" button for uploading more images
                            <div
                                {...getRootProps()}
                                className={classNames(classes.multiImages.base, classes.multiImages.addButton)}>
                                <input {...getInputProps()} />
                                <PlusLg size={26} className="text-primary-color" />
                            </div>
                        ) : null}
                    </NativeFileDropTarget>
                </div>
                <div>
                    {renderDescription()}
                    {props.error ? <FormError className={classes.error}>{props.error}</FormError> : null}
                </div>
            </div>
        )
    }

    // Render layout for single uploaded image
    if (!!props.images.length && !props.loadingImages?.length) {
        const image = head(props.images) as ImageUploadData
        return (
            <div className={classNames(classes.wrap, classes.isDone, props.className)}>
                <button
                    type="button"
                    className={classes.previewBtn}
                    onClick={() =>
                        props.allowEditUploadedImages ? props.openImageEdit(image) : props.openImagePreview(image)
                    }>
                    <img src={image.originalImage.url} alt="Image thumbnail" className={classes.image} />
                </button>
                {props.allowEditUploadedImages ? (
                    <Button className={classes.editBtn} onClick={() => props.openImageEdit(image)}>
                        <AntdIconWrap type="edit" className={classes.deleteIcon} />
                    </Button>
                ) : null}
                <Button className={classes.closeBtn} onClick={() => props.onRemove(image.originalImage.id)}>
                    <AntdIconWrap type="delete" className={classes.deleteIcon} />
                </Button>
            </div>
        )
    }

    // Render layout for uploading single image
    if (!!props.loadingImages?.length) {
        return renderUploadAreaLoading()
    } else {
        // Render empty state
        return renderUploadArea()
    }
}

const classes = {
    multiImages: {
        wrap: "flex flex-wrap items-center",
        base: "flex flex-col justify-center items-center relative cursor-pointer h-[80px] w-[80px] mr-2.5 mb-2.5",
        loading: "px-4 pt-1 rounded-md border border-dashed border-gray-300 overflow-hidden",
        img: "w-full h-full object-cover",
        imgButton:
            "cursor-pointer p-0 block w-full h-full overflow-hidden rounded-md border border-gray-300 hover:border-primary-color",
        addButton: "overflow-hidden rounded-md border border-dashed border-gray-300 hover:border-primary-color",
    },
    multiImagesWithLabel: {
        wrap: "flex flex-col border border-gray-200 rounded-[6px] divide-y divide-gray-200",
        row: "flex items-center h-[62px]",
        dragWrap: "border-r border-gray-200 flex-none w-[46px] self-stretch flex items-center justify-center",
        dragIcon: "w-auto h-[24px]",
        imgButton:
            "flex items-center justify-center h-[80%] w-[68px] rounded-[4px] p-0 mx-4 bg-transparent cursor-pointer hover:opacity-80 transition",
        imgPreview: "block w-full h-full object-contain",
        removeBtn:
            "flex flex-col items-center justify-center rounded-full h-[28px] w-[28px] !p-0 leading-none hover:text-red-500 hover:border-red-500 pt-px flex-none mr-4",
    },
    wrap: "flex flex-col justify-center relative w-full rounded-lg text-center border border-dashed px-4 h-[50px]",
    isEmpty: "cursor-pointer border-gray-300 bg-white hover:bg-gray-50/50",
    isLoading: "border-gray-300 bg-white",
    isDragging: "border-sky-400 bg-sky-50",
    isDone: "cursor-pointer border-gray-300 border-solid bg-white hover:border-primary-color p-0 h-[70px]",
    row: "flex items-center justify-center",
    btn: "mr-1.5 h-[24px]",
    closeBtn:
        "absolute right-[-5px] bottom-[-5px] flex flex-col items-center justify-center rounded-full h-[28px] w-[28px] !p-0 leading-none hover:text-red-500 hover:border-red-500 pt-px",
    editBtn:
        "absolute right-[28px] bottom-[-5px] flex flex-col items-center justify-center rounded-full h-[28px] w-[28px] !p-0 leading-none hover:text-primary-color hover:border-primary-color pt-px",
    deleteIcon: "text-[16px]",
    label: "text-14",
    desc: "mt-1 text-13 opacity-50",
    error: "",
    image: "w-full h-full object-contain",
    previewBtn: "p-0 block cursor-pointer bg-transparent overflow-hidden rounded-lg",
}
